import { useRouter } from 'next/router'
import { NextPageWithLayout } from '../_app'
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next'
import { DEFAULT_LOCALE } from '~/config/constants'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { QueryClient, dehydrate } from '@tanstack/react-query'
import { createLanguageRegionLocale } from '~/lib/createLanguageRegionLocale'
import { createGraphQLClient } from '~/graphql/contentfulClient'
import { config } from '~/lib/queryClientInit'
import {
  HeaderQuery,
  MobileHeaderQuery,
  NavigationQuery,
  PageContentModulesCollection,
  PreContentQuery,
  StageDocument,
  useHeaderQuery,
  useMobileHeaderQuery,
  useNavigationQuery,
  usePreContentQuery,
  useStageQuery,
} from '@contentfulTypes'
import { contentQueryDocs } from '../[...slug]'
import { log } from 'console'
import Layout from '~/modules/Layout/Layout'
import { CorportationJsonLd } from '~/modules/StructuredData/CorporationJsonLd'
import { WebsisteJsonLd } from '~/modules/StructuredData/WebsiteJsonLd'
import { CioCmsPage } from '~/modules/SearchResultPageCio/CioCmsPage'
import { VariationsStrategy } from '~/modules/SearchResultPageCio/lib/constants'

type Prop = {
  cioCollectionId: string | null
  cioGroupId: string | null
  cioShowVariationsStrategy: string | null
}

const Page: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = ({
  cioCollectionId,
  cioGroupId,
  cioShowVariationsStrategy,
}) => {
  const router = useRouter()
  const query = router.query['query'] as string[]
  const stringQuery = Array.isArray(query) ? query.join('/') : query

  return (
    <>
      <CioCmsPage
        slug={`enhancedsearch/${encodeURIComponent(stringQuery)}`}
        filterName={cioCollectionId ? 'collection_id' : 'group_id'}
        filterValue={(cioCollectionId || cioGroupId) as string}
        showVariationsStrategy={(cioShowVariationsStrategy as VariationsStrategy) || undefined}
      />
      <CorportationJsonLd />
      <WebsisteJsonLd />
    </>
  )
}

Page.getLayout = (page) => <Layout>{page}</Layout>

export const getStaticProps: GetStaticProps<Prop> = async ({
  locale = DEFAULT_LOCALE,
  params,
  preview = false,
}) => {
  const finalLocale = createLanguageRegionLocale(locale)
  const queryClient = new QueryClient(config)
  const contentfulClient = createGraphQLClient({ preview })
  const query = (params?.query as string[])?.join('/')

  /*** ----- START: prefetching navigation ----- ***/
  await queryClient.prefetchQuery<NavigationQuery>(
    useNavigationQuery.getKey({ preview, locale: finalLocale }),
    async () =>
      await contentfulClient.request(useNavigationQuery.document, {
        preview,
        locale: finalLocale,
      }),
  )
  /*** ----- END: prefetching navigation ----- ***/

  /*** ----- START: prefetching navigation contents ----- ***/
  try {
    await queryClient.fetchQuery<HeaderQuery>(
      useHeaderQuery.getKey({ preview, locale: finalLocale }),
      async () =>
        await contentfulClient.request(useHeaderQuery.document, {
          preview,
          locale: finalLocale,
        }),
    )

    await queryClient.fetchQuery<MobileHeaderQuery>(
      useMobileHeaderQuery.getKey({ preview, locale: finalLocale }),
      async () =>
        await contentfulClient.request(useMobileHeaderQuery.document, {
          preview,
          locale: finalLocale,
        }),
    )
  } catch (error) {
    console.log(error)
  }
  /*** ----- END: prefetching navigation contents ----- ***/

  /*** ----- START: prefetching cms contents ----- ***/
  let cioCollectionId: string | null = null
  let cioGroupId: string | null = null
  let cioShowVariationsStrategy: string | null = null
  try {
    const prefetchContent = async (page: string) => {
      let isThisPageExist = false

      try {
        const preFetchContentPage = await queryClient.fetchQuery<PreContentQuery>(
          usePreContentQuery.getKey({ slug: page, preview, locale: finalLocale }),
          async () =>
            await contentfulClient.request(usePreContentQuery.document, {
              slug: page,
              preview,
              locale: finalLocale,
            }),
        )

        isThisPageExist = (preFetchContentPage?.pageCollection?.items?.length || 0) > 0

        if (isThisPageExist) {
          const fetchContent = async (page: PageContentModulesCollection['items'][0]) => {
            if (!contentQueryDocs?.[page?.__typename as keyof typeof contentQueryDocs]) return
            await queryClient.prefetchQuery(
              [page?.__typename, { id: page?.sys?.id, preview, locale: finalLocale }],
              async () =>
                await contentfulClient.request(
                  contentQueryDocs?.[page?.__typename as keyof typeof contentQueryDocs],

                  {
                    id: page?.sys?.id,
                    preview,
                    locale: finalLocale,
                  },
                ),
            )
          }
          const contentItems =
            preFetchContentPage?.pageCollection?.items?.[0]?.contentModulesCollection?.items

          if (contentItems) {
            await Promise.all(
              contentItems.map(async (page) => {
                await fetchContent(page as PageContentModulesCollection['items'][0])
              }),
            )
          }

          if (preFetchContentPage?.pageCollection?.items?.[0]?.stage) {
            await queryClient.prefetchQuery(
              useStageQuery.getKey({
                id: preFetchContentPage?.pageCollection?.items?.[0]?.stage.sys?.id,
                preview,
                locale: finalLocale,
              }),
              async () =>
                await contentfulClient.request(StageDocument, {
                  id: preFetchContentPage?.pageCollection?.items?.[0]?.stage?.sys?.id,
                  preview,
                  locale: finalLocale,
                }),
            )
          }
        }
      } catch (error) {
        console.log(error)
      }

      return isThisPageExist
    }

    const preFetchContentPage = await queryClient.fetchQuery<PreContentQuery>(
      usePreContentQuery.getKey({
        slug: `enhancedsearch/${encodeURIComponent(query)}`,
        preview,
        locale: finalLocale,
      }),
      async () =>
        await contentfulClient.request(usePreContentQuery.document, {
          slug: `enhancedsearch/${encodeURIComponent(query)}`,
          preview,
          locale: finalLocale,
        }),
    )

    if (process.env.NEXT_PUBLIC_CONSTRUCTORIO_API_KEYS) {
      cioCollectionId = preFetchContentPage?.pageCollection?.items?.[0]?.cioCollectionId ?? null
      cioGroupId = preFetchContentPage?.pageCollection?.items?.[0]?.cioGroupId ?? null
      cioShowVariationsStrategy =
        preFetchContentPage?.pageCollection?.items?.[0]?.cioShowVariationsStrategy ?? null
    }

    const fetchContent = async (page: PageContentModulesCollection['items'][0]) => {
      if (!contentQueryDocs?.[page?.__typename as keyof typeof contentQueryDocs]) return
      await queryClient.prefetchQuery(
        [page?.__typename, { id: page?.sys?.id, preview, locale: finalLocale }],
        async () =>
          await contentfulClient.request(
            contentQueryDocs?.[page?.__typename as keyof typeof contentQueryDocs],

            {
              id: page?.sys?.id,
              preview,
              locale: finalLocale,
            },
          ),
      )
    }
    const contentItems =
      preFetchContentPage?.pageCollection?.items?.[0]?.contentModulesCollection?.items

    if (contentItems) {
      await Promise.all(
        contentItems.map(async (page) => {
          await fetchContent(page as PageContentModulesCollection['items'][0])
        }),
      )
    }

    await prefetchContent(`header-${encodeURIComponent(query)}`)
  } catch (error) {
    log(error)
  }
  /*** ----- END: prefetching cms contents ----- ***/

  if (process.env.NEXT_PUBLIC_CONTENTFUL_ENV === 'stage') {
    return {
      props: {
        ...(await serverSideTranslations(finalLocale, ['common', 'geopopup'])),
        // yes this is weird, but
        // https://github.com/vercel/next.js/discussions/11209#discussioncomment-35915
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
        cioCollectionId,
        cioGroupId,
        cioShowVariationsStrategy,
        preview,
      },
      revalidate: 10,
    }
  } else {
    return {
      props: {
        ...(await serverSideTranslations(finalLocale, ['common', 'geopopup'])),
        // yes this is weird, but
        // https://github.com/vercel/next.js/discussions/11209#discussioncomment-35915
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
        cioCollectionId,
        cioGroupId,
        cioShowVariationsStrategy,
        preview,
      },
    }
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return { paths: [{ params: { query: ['/'] } }], fallback: 'blocking' }
}

export default Page
